import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { App, Button, Drawer, Form, Input, Select, Space, Switch } from 'antd';
import type { FormListFieldData } from 'antd/es/form/FormList';
import { ReactNode, useState } from 'react';
import CustomizedUpload from 'components/customized-upload';
import { UPLOAD_TYPE } from 'constants/common';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { saveBlock } from '../api';
import {
  BLOCK_KIND_META,
  BlockInput,
  GalaBlock,
  GalaBlockKind,
  GalaBlockVisibility,
  VISIBILITY_OPTIONS,
  emptyBlockData,
  errorText,
  groupMatches,
} from '../contract';
import styles from '../styles.module.scss';

const { TextArea } = Input;

type UploadValue = { fileKey: string; fileUrl: string; error?: string };

interface FormValues {
  title?: string;
  body?: string;
  visibility: GalaBlockVisibility;
  groups?: string[];
  isHidden: boolean;
  // theo loại khối
  phases?: { step?: string; title?: string; period?: string; body?: string; color?: string }[];
  images?: { image?: UploadValue; caption?: string; ratio?: 'tall' | 'wide' | 'square' }[];
  pill?: string;
  note?: string;
  boxes?: { value?: string; label?: string }[];
  rows?: { label?: string; value?: string; minus?: boolean }[];
  icon?: string;
  items?: { time?: string; title?: string; note?: string }[];
  url?: string;
  label?: string;
}

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_PHASE_COLORS = ['#FE2C55', '#1BB9D8', '#E5399B'];
const RATIO_OPTIONS = [
  { value: 'tall', label: 'Dọc' },
  { value: 'wide', label: 'Ngang' },
  { value: 'square', label: 'Vuông' },
];

const clean = (v?: string | null) => (v ?? '').trim();
const blankToNull = (v?: string | null) => clean(v) || null;

/** Danh sách con có nút lên / xuống / xoá cho từng mục (Form.List). */
const SubList = ({
  name,
  addLabel,
  addValue,
  render,
}: {
  name: string;
  addLabel: string;
  addValue: Record<string, unknown>;
  render: (field: FormListFieldData, index: number) => ReactNode;
}) => (
  <Form.List name={name}>
    {(fields, { add, remove, move }) => (
      <>
        <div className={styles.subList}>
          {fields.map((field, index) => (
            <div key={field.key} className={styles.subItem}>
              <div className={styles.subItemTools}>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  aria-label="Dời lên"
                  onClick={() => move(index, index - 1)}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === fields.length - 1}
                  aria-label="Dời xuống"
                  onClick={() => move(index, index + 1)}
                />
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Xoá mục này"
                  onClick={() => remove(field.name)}
                />
              </div>
              {render(field, index)}
            </div>
          ))}
        </div>
        <Button icon={<PlusOutlined />} onClick={() => add(addValue)}>
          {addLabel}
        </Button>
      </>
    )}
  </Form.List>
);

const imageValue = (key?: string): UploadValue | undefined =>
  key ? { fileKey: key, fileUrl: `${SERVER_FILE_ENDPOINT}/${key}` } : undefined;

function toFormValues(kind: GalaBlockKind, block: GalaBlock | null): FormValues {
  const d = (block?.data ?? emptyBlockData(kind) ?? {}) as Record<string, any>;
  return {
    title: block?.title ?? '',
    body: block?.body ?? '',
    visibility: block?.visibility ?? 'ALL',
    groups: block?.groups ?? [],
    isHidden: block?.isHidden ?? false,
    phases: (d.phases ?? []).map((p: any) => ({ ...p })),
    images: (d.images ?? []).map((img: any) => ({
      image: imageValue(img?.key),
      caption: img?.caption ?? '',
      ratio: img?.ratio ?? 'tall',
    })),
    pill: d.pill ?? '',
    note: d.note ?? '',
    boxes: (d.boxes ?? []).map((b: any) => ({ ...b })),
    rows: (d.rows ?? []).map((r: any) => ({ ...r, minus: !!r.minus })),
    icon: d.icon ?? '🔔',
    items: (d.items ?? []).map((i: any) => ({ ...i })),
    url: d.url ?? '',
    label: d.label ?? '',
  };
}

/** Dựng `data` đúng dạng của từng loại khối (docs/gala/01-dac-ta §3.2). */
function toData(kind: GalaBlockKind, v: FormValues): Record<string, any> | null {
  switch (kind) {
    case 'ROADMAP':
      return {
        phases: (v.phases ?? []).map((p, i) => ({
          step: clean(p.step),
          title: clean(p.title),
          period: clean(p.period),
          body: clean(p.body),
          color: clean(p.color) || DEFAULT_PHASE_COLORS[i % DEFAULT_PHASE_COLORS.length],
        })),
      };
    case 'GALLERY':
      return {
        images: (v.images ?? []).flatMap((i) =>
          i.image?.fileKey
            ? [{ key: i.image.fileKey, caption: clean(i.caption), ratio: i.ratio ?? 'tall' }]
            : []
        ),
      };
    case 'TALLY':
      return {
        pill: clean(v.pill),
        note: clean(v.note),
        boxes: (v.boxes ?? []).map((b) => ({ value: clean(b.value), label: clean(b.label) })),
        rows: (v.rows ?? []).map((r) => ({
          label: clean(r.label),
          value: clean(r.value),
          minus: !!r.minus,
        })),
      };
    case 'NOTICE':
      return { icon: clean(v.icon) || '🔔' };
    case 'SCHEDULE':
      return {
        items: (v.items ?? []).map((i) => ({
          time: clean(i.time),
          title: clean(i.title),
          note: clean(i.note),
        })),
      };
    case 'LINK':
      return { url: clean(v.url), label: clean(v.label) };
    default:
      return null;
  }
}

interface Props {
  open: boolean;
  eventId: number;
  /** Khối đang sửa; null = thêm mới theo `kind`. */
  block: GalaBlock | null;
  kind: GalaBlockKind;
  /** Gợi ý mã nhóm khách (nhóm có sẵn + nhóm đang dùng). */
  groupOptions: string[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

/** Ngăn kéo thêm / sửa một khối nội dung; ô nhập đổi theo `kind`. */
const BlockForm = ({ open, eventId, block, kind, groupOptions, onClose, onSaved }: Props) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const visibility = Form.useWatch('visibility', form);

  const onOk = async () => {
    const v = await form.validateFields();
    const input: BlockInput = {
      kind,
      title: blankToNull(v.title),
      body: blankToNull(v.body),
      data: toData(kind, v),
      visibility: v.visibility,
      groups:
        v.visibility === 'GROUPS'
          ? (v.groups ?? []).map((g) => g.trim().toUpperCase()).filter(Boolean)
          : null,
      isHidden: v.isHidden,
    };
    setSaving(true);
    try {
      await saveBlock(eventId, input, block?.id);
      message.success(block ? 'Đã lưu khối.' : 'Đã thêm khối.');
      await onSaved();
      onClose();
    } catch (e) {
      message.error(errorText(e, 'Không lưu được khối. Kiểm tra lại các ô rồi thử lại.'));
    } finally {
      setSaving(false);
    }
  };

  const meta = BLOCK_KIND_META[kind];
  const bodyLabel = kind === 'TEXT' || kind === 'NOTICE' ? 'Nội dung' : 'Mô tả ngắn (không bắt buộc)';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      title={`${block ? 'Sửa' : 'Thêm'} khối: ${meta.text}`}
      destroyOnHidden
      maskClosable={false}
      // Form dựng lại mỗi lần mở; reset thêm sau khi mở để chắc chắn lấy giá trị của khối đang chọn.
      afterOpenChange={(opened) => opened && form.resetFields()}
      extra={
        <Space>
          <Button onClick={onClose}>Huỷ</Button>
          <Button type="primary" loading={saving} onClick={onOk}>
            Lưu
          </Button>
        </Space>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        initialValues={toFormValues(kind, block)}
        preserve={false}
        requiredMark="optional"
      >
        <p className={styles.hint} style={{ marginTop: 0 }}>
          {meta.hint}
        </p>

        <Form.Item name="title" label="Tiêu đề" rules={[{ max: 200, message: 'Không quá 200 ký tự.' }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="body"
          label={bodyLabel}
          extra={'Xuống dòng được giữ nguyên; bọc **chữ** để in đậm.'}
        >
          <TextArea autoSize={{ minRows: 3, maxRows: 12 }} />
        </Form.Item>

        {kind === 'ROADMAP' && (
          <Form.Item label="Các chặng" required={false}>
            <SubList
              name="phases"
              addLabel="Thêm chặng"
              addValue={{ color: DEFAULT_PHASE_COLORS[0] }}
              render={(field) => (
                <>
                  <div className={styles.row2}>
                    <Form.Item name={[field.name, 'step']} label="Tên chặng">
                      <Input placeholder="Chặng 1" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'period']} label="Thời gian">
                      <Input placeholder="Tháng 1 – Tháng 11/2026" />
                    </Form.Item>
                  </div>
                  <Form.Item name={[field.name, 'title']} label="Tiêu đề chặng">
                    <Input />
                  </Form.Item>
                  <Form.Item name={[field.name, 'body']} label="Nội dung">
                    <TextArea autoSize={{ minRows: 3, maxRows: 10 }} />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'color']}
                    label="Màu chặng"
                    rules={[{ pattern: HEX_COLOR, message: 'Nhập mã màu dạng #FE2C55.' }]}
                  >
                    <Input style={{ maxWidth: 160 }} placeholder="#FE2C55" />
                  </Form.Item>
                </>
              )}
            />
          </Form.Item>
        )}

        {kind === 'GALLERY' && (
          <Form.Item label="Ảnh" required={false}>
            <SubList
              name="images"
              addLabel="Thêm ảnh"
              addValue={{ ratio: 'tall' }}
              render={(field) => (
                <>
                  <Form.Item
                    name={[field.name, 'image']}
                    rules={[
                      {
                        validator: (_: unknown, val?: UploadValue) =>
                          val?.error
                            ? Promise.reject(new Error(val.error))
                            : val?.fileKey
                            ? Promise.resolve()
                            : Promise.reject(new Error('Chọn ảnh.')),
                      },
                    ]}
                  >
                    <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
                  </Form.Item>
                  <div className={styles.row2}>
                    <Form.Item name={[field.name, 'caption']} label="Chú thích">
                      <Input />
                    </Form.Item>
                    <Form.Item name={[field.name, 'ratio']} label="Khung ảnh">
                      <Select options={RATIO_OPTIONS} />
                    </Form.Item>
                  </div>
                </>
              )}
            />
          </Form.Item>
        )}

        {kind === 'TALLY' && (
          <>
            <Form.Item name="pill" label="Nhãn nổi bật">
              <Input placeholder="Chỉ 100 slot tham dự" />
            </Form.Item>
            <Form.Item label="Ô số liệu lớn" required={false}>
              <SubList
                name="boxes"
                addLabel="Thêm ô số liệu"
                addValue={{}}
                render={(field) => (
                  <div className={styles.row2}>
                    <Form.Item name={[field.name, 'value']} label="Số liệu">
                      <Input placeholder="10" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'label']} label="Nhãn">
                      <Input placeholder="Hạng mục" />
                    </Form.Item>
                  </div>
                )}
              />
            </Form.Item>
            <Form.Item label="Bảng dòng" required={false}>
              <SubList
                name="rows"
                addLabel="Thêm dòng"
                addValue={{ minus: false }}
                render={(field) => (
                  <>
                    <div className={styles.row2}>
                      <Form.Item name={[field.name, 'label']} label="Nội dung dòng">
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, 'value']} label="Giá trị">
                        <Input placeholder="14–15 người" />
                      </Form.Item>
                    </div>
                    <Form.Item
                      name={[field.name, 'minus']}
                      label="Dòng trừ"
                      valuePropName="checked"
                      extra="Bật thì dòng này hiện kiểu số bị trừ đi."
                    >
                      <Switch />
                    </Form.Item>
                  </>
                )}
              />
            </Form.Item>
            <Form.Item name="note" label="Ghi chú cuối bảng">
              <TextArea autoSize={{ minRows: 2, maxRows: 8 }} />
            </Form.Item>
          </>
        )}

        {kind === 'NOTICE' && (
          <Form.Item
            name="icon"
            label="Biểu tượng"
            extra="Một emoji, ví dụ 🔔 hoặc ⚠️."
            rules={[{ max: 8, message: 'Chỉ một biểu tượng.' }]}
          >
            <Input style={{ maxWidth: 120 }} />
          </Form.Item>
        )}

        {kind === 'SCHEDULE' && (
          <Form.Item label="Các mốc giờ" required={false}>
            <SubList
              name="items"
              addLabel="Thêm mốc"
              addValue={{}}
              render={(field) => (
                <>
                  <div className={styles.row2}>
                    <Form.Item name={[field.name, 'time']} label="Giờ">
                      <Input placeholder="18:00" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'title']} label="Nội dung">
                      <Input />
                    </Form.Item>
                  </div>
                  <Form.Item name={[field.name, 'note']} label="Ghi chú">
                    <Input />
                  </Form.Item>
                </>
              )}
            />
          </Form.Item>
        )}

        {kind === 'LINK' && (
          <>
            <Form.Item
              name="url"
              label="Đường dẫn"
              rules={[
                { required: true, whitespace: true, message: 'Nhập đường dẫn.' },
                { pattern: /^https?:\/\//i, message: 'Đường dẫn phải bắt đầu bằng http:// hoặc https://' },
              ]}
            >
              <Input placeholder="https://..." />
            </Form.Item>
            <Form.Item
              name="label"
              label="Chữ trên nút"
              rules={[{ required: true, whitespace: true, message: 'Nhập chữ trên nút.' }]}
            >
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item name="visibility" label="Ai thấy">
          <Select options={VISIBILITY_OPTIONS} />
        </Form.Item>
        {visibility === 'GROUPS' && (
          <Form.Item
            name="groups"
            label="Mã nhóm khách được thấy"
            // In hoa ngay khi gõ, bỏ trùng (gõ "khách mời" và chọn "KHÁCH MỜI" chỉ còn một)
            getValueFromEvent={(v: string[]) =>
              Array.from(new Set(v.map((g) => g.trim().toUpperCase()).filter(Boolean)))
            }
            extra="Chọn nhóm có sẵn, hoặc gõ mã nhóm khác rồi Enter. Chữ thường sẽ tự đổi sang in hoa."
            rules={[
              {
                validator: (_: unknown, val?: string[]) =>
                  val && val.length ? Promise.resolve() : Promise.reject(new Error('Nhập ít nhất một mã nhóm.')),
              },
            ]}
          >
            {/* Không tách theo dấu cách: nhóm như "GROUP DANCE", "KHÁCH MỜI" có dấu cách. */}
            <Select
              mode="tags"
              tokenSeparators={[',']}
              options={groupOptions.map((g) => ({ value: g, label: g }))}
              filterOption={(input, opt) => groupMatches(input, String(opt?.value ?? ''))}
              placeholder="Chọn hoặc gõ nhóm"
            />
          </Form.Item>
        )}
        <Form.Item name="isHidden" label="Ẩn khối này" valuePropName="checked" extra="Ẩn thì app không hiện khối.">
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default BlockForm;
